import { authApi, siteApi } from "@api";
import { IconMaterialIdCard } from "@assets/icons";
import RHFController from "@components/inputs/RHFController";
import MemberSettingsRenderer from "@components/utils/MemberSettingsRenderer";
import { ValidatorSchema } from "@constants/validators";
import { zodResolver } from "@hookform/resolvers/zod";
import useCaptureFormValidationErrors from "@hooks/useCaptureFormValidationErrors";
import { useMemberSettings } from "@hooks/useMemberSettings";
import { AppDispatch } from "@stores";
import authSlice from "@stores/auth/slices";
import { buildPersonalInfoRequest } from "@utils/personal-info";
import {
  Button,
  MemberSettingNameMode,
  TextField,
  UpdatePersonalInfoRequest,
  useFieldHelperText,
  useHandleApiResponse,
} from "kz-ui-sdk";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { z } from "zod";

const legalNameFormSchema = z.object({
  fullName: ValidatorSchema.legalName,
  firstname: ValidatorSchema.firstName,
  lastname: ValidatorSchema.lastName,
});

type FormValues = z.infer<typeof legalNameFormSchema>;

interface FormLegalNameProps {
  onUpdateSuccess?: () => void;
  mode: "update" | "add";
}

const FormLegalName = ({ onUpdateSuccess, mode }: FormLegalNameProps) => {
  const { t } = useTranslation();
  const { handleApiResponse } = useHandleApiResponse({
    toast,
  });

  const [updatePersonalInfo, { isLoading: isSubmitting }] = siteApi.useUpdatePersonalInfoMutation();
  const dispatch = useDispatch<AppDispatch>();
  const { nameMode } = useMemberSettings();

  const formSchema = useMemo(() => {
    switch (nameMode) {
      case MemberSettingNameMode.FirstLast:
        return legalNameFormSchema.omit({ fullName: true });
      case MemberSettingNameMode.FullInFirst:
      default:
        return legalNameFormSchema.omit({ firstname: true, lastname: true });
    }
  }, [nameMode]);

  const form = useForm<FormValues>({
    mode: "onChange",
    resolver: zodResolver(formSchema),
    defaultValues: {
      fullName: "",
    },
  });

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, submitCount },
    control,
    setValue,
    trigger,
  } = form;

  // Capture form validation errors
  useCaptureFormValidationErrors(form, { feature: "legal-name-update" });

  const fieldHelperTextManager = useFieldHelperText({
    submitCount: submitCount,
    trigger: trigger,
    errors: errors,
  });

  const onSubmit = async (data: FormValues) => {
    const request: UpdatePersonalInfoRequest = buildPersonalInfoRequest(
      {
        firstName: data.firstname,
        lastName: data.lastname,
        accountName: data.fullName,
      },
      nameMode,
    );

    const result = await updatePersonalInfo(request);
    handleApiResponse(result, {
      successMessage: t("name update success"),
      onSuccess: async () => {
        // refresh self
        const result = await dispatch(
          authApi.endpoints.getProfile.initiate(undefined, {
            forceRefetch: true,
          }),
        );
        if (result.data) {
          dispatch(authSlice.actions.updateProfile(result.data));
        }
        onUpdateSuccess?.();
      },
    });
  };

  return (
    <form
      id="form-legal-name"
      name="form-legal-name"
      onSubmit={handleSubmit(onSubmit)}
      className="flex flex-col gap-4"
      autoComplete="off"
    >
      <MemberSettingsRenderer
        feature="nameMode"
        match={MemberSettingNameMode.FullInFirst}
      >
        <div className="flex flex-col gap-4">
          <TextField
            placeholder={t("Full name")}
            {...register("fullName")}
            error={!!errors.fullName}
            helperText={t(errors.fullName?.message || "")}
            size="sm"
            icon={<IconMaterialIdCard />}
            classes={{
              "input&": "w-full !px-0 !ps-11 text-start",
              "iconContainer&": "!start-3 !top-[10px]",
            }}
            fieldHelperTextManager={fieldHelperTextManager}
          />
        </div>
      </MemberSettingsRenderer>

      {/* FIRST NAME & LAST NAME */}
      <MemberSettingsRenderer
        feature="nameMode"
        match={MemberSettingNameMode.FirstLast}
      >
        <div className="flex flex-col gap-3">
          <RHFController
            control={control}
            name="firstname"
            onBlur={(v) => {
              setValue("firstname", v.trim());
            }}
          >
            <TextField
              classes={{
                "input&": "w-full truncate",
              }}
              placeholder={t("First name")}
              disabled={isSubmitting}
              size={"lg"}
            />
          </RHFController>
          <RHFController
            control={control}
            name="lastname"
            onBlur={(v) => {
              setValue("lastname", v.trim());
            }}
          >
            <TextField
              classes={{
                "input&": "w-full truncate",
              }}
              placeholder={t("Last name")}
              disabled={isSubmitting}
              size={"lg"}
            />
          </RHFController>
        </div>
      </MemberSettingsRenderer>
      <Button
        type="submit"
        variant="primary"
        className="w-full"
        loading={isSubmitting}
        disabled={isSubmitting}
        size="lg"
      >
        {mode === "update" ? t("Update") : t("finish & login")}
      </Button>
    </form>
  );
};

export default FormLegalName;
